import type { PeopleGroupParticipantType } from "@app/types/api/people";
import { z } from "zod";

export const chatUnreadCap = 20;

export type ChatKindType = "direct" | "topic" | "routine" | "support";

export type ChatPreviewType = { senderName: string; text: string };

export type ChatSummaryType = {
  sId: string;
  kind: ChatKindType;
  title: string | null;
  label: string;
  participantIds: string[];
  participants: PeopleGroupParticipantType[];
  preview: ChatPreviewType | null;
  canCompose: boolean;
  pinned: boolean;
  lastActivityAt: number;
  unreadCount: number;
  latestConversationId: string;
  triggerId: string | null;
};

export type GetChatResponseBody = { chat: ChatSummaryType };

export type GetChatsResponseBody = {
  pinned: ChatSummaryType[];
  recent: ChatSummaryType[];
  memoair?: ChatSummaryType[];
};

export type ChatSegmentType = {
  conversationId: string;
  title: string | null;
  triggered: boolean;
  createdAt: number;
  updatedAt: number;
  unread: boolean;
};

export type GetChatSegmentsResponseBody = {
  segments: ChatSegmentType[];
  hasMore: boolean;
  nextCursor: number | null;
};

export const GetChatSegmentsQuerySchema = z.object({
  cursor: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});

export const PostChatSegmentBodySchema = z.object({
  segmentConversationId: z.string().min(1).nullable(),
});

export type PostChatSegmentResponseBody = { conversationId: string };

export const ChatMatchQuerySchema = z.object({
  participants: z.string().min(1),
});

export type GetChatMatchesResponseBody = { chats: ChatSummaryType[] };

export const CreateChatBodySchema = z.object({
  participantIds: z.array(z.string().min(1)).max(20),
  title: z.string().trim().min(1).max(200).optional(),
});

export type CreateChatResponseBody = { chatId: string };

export const PatchChatBodySchema = z.object({
  title: z.string().trim().min(1).max(200),
});

export const PatchChatMembershipBodySchema = z
  .object({
    pinned: z.boolean().optional(),
    read: z.literal(true).optional(),
  })
  .refine((body) => body.pinned !== undefined || body.read !== undefined);

export const ChatParamsSchema = z.object({ chatId: z.string().min(1) });

export const ChatSegmentParamsSchema = ChatParamsSchema.extend({
  segmentConversationId: z.string().min(1),
});
